import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactTable from 'react-table';
import { LazySelect } from '../ReactProtos/CellSelect';
import useLocalStorageState from 'use-local-storage-state';
import Button from '@material-ui/core/Button';

async function search(collection, query, sort, limit) {
    const response = await fetch(`/api/data/search/${collection}`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            query,
            sort,
            limit,
        }),
    });
    const json = await response.json();
    return json.results;
}

async function aggregate(collection, ...pipeline) {
    const response = await fetch(`/api/data/aggregate/${collection}`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            pipeline,
        }),
    });
    const json = await response.json();
    return json.results;
}

function formatSeasonLable(season) {
    const status = season.status === 0 ? 'not started' : (season.status === 1 ? 'in progress' : 'finished');
    const startTime = new Date(season.start_time).toLocaleString();
    return `${season._id} ${status} ${startTime})`;
}

const SeasonContext = createContext('season');

const userNameCache = {};

function User({uuid, name: providedName}) {
    const [name, setName] = useState(providedName);
    const season = useContext(SeasonContext);
    useEffect(() => {
        if (name) {
            return;
        }
        if (userNameCache[uuid]) {
            setName(userNameCache[uuid]);
            return;
        }
        async function run() {
            const names = await search('user', {
                _id: uuid,
            }, {}, 1);
            const name = names[0].name;
            userNameCache[uuid] = name;
            setName(name);
        }
        run();
    }, []);
    if (uuid.startsWith('bot:') || uuid.startsWith('b:')) {
        return <span>{name ? `${name} (${uuid})` : uuid} (<span style={{color: 'red'}}>bot!</span>)</span>;
    }
    return <a href={`/async_pvp_user?season=${season}&uuid=${uuid}`}>{name ? `${name} (${uuid})` : uuid}</a>;
}

function Win({text='Win'}) {
    return <span style={{color: 'green'}}>{text}</span>;
}

function Lose({text = 'Lose'}) {
    return <span style={{color: 'red'}}>{text}</span>;
}

function Result({value}) {
    return value === 1 ? <Win /> : (value === -1 ? <Lose /> : 'other');
}

function Change({value}) {
    return value > 0 ? <Win text={value} /> : <Lose text={value} />;
}

function SessionInfo({session}) {
    const [changes, setChanges] = useState([]);
    useEffect(() => {
        async function getChanges() {
            const attacks = await search('async_pvp_elo_history', {
                season: session.season,
                uuid: session.uuid,
                session: session._id,
            }, {}, 5);
            const defenses = [];
            for (const opponent of session.finished_opponents) {
                const defense = await search('async_pvp_elo_history', {
                    season: session.season,
                    uuid: opponent.uuid,
                    session: session._id,
                }, {}, 1);
                if (defense.length > 0) {
                    defenses.push(defense[0]);
                }
            }
            setChanges([...attacks, ...defenses]);
        }
        getChanges();
    }, []);
    const status = session.status === 0 ? 'Playing' : 'Finished';
    return <div>
        <div>User: <User uuid={session.uuid} /></div>
        <div>Time: {new Date(session.start_time).toLocaleString()}</div>
        <div>Status: {status}</div>
        <ul>
            {
                session.finished_opponents.map((opponent, i) => {
                    const result = session.results[i];
                    return <li key={i}>
                        <User season={session.season} uuid={opponent.uuid} name={opponent.name} /> === <Result value={result} />
                    </li>;
                })
            }
            {
                session.status === 0 && session.current_opponent &&
                <li>
                    <User season={session.season} uuid={session.current_opponent.uuid} name={session.current_opponent.name} />
                </li>
            }
        </ul>
        <div>Elo Changes:</div>
        <ul>
            {
                changes.map((change, i) => {
                    return <li key={i}>
                        <User uuid={change.uuid} /> Elo: <Change value={change.change} /> MMR: <Change value={change.match_making_rating_change} />
                    </li>;
                })
            }
        </ul>
    </div>
}

function ProtectedUntil({value}) {
    const date = new Date(value);
    if (date.getTime() > Date.now()) {
        return `Protected until ${date.toLocaleString()}`;
    }
    return 'Not protected';
}

function safeFormatJson(text) {
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        return text;
    }
}

function UserComponent({uuid}) {
    const [userInfo, setUserInfo] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [defenses, setDefenses] = useState([]);
    const [dailyRanks, setDailyRanks] = useState([]);
    const [seasonRank, setSeasonRank] = useState(0);
    const season = useContext(SeasonContext);
    const user = {season, uuid};
    console.log('showing user', user, userInfo);
    useEffect(() => {
        async function getUserInfo() {
            const users = await search('async_pvp_season_user', {
                season: user.season,
                uuid: user.uuid,
            }, {}, 1);
            setUserInfo(users[0]);
        }
        async function getSessions() {
            const sessions = await search('async_pvp_session', {
                season: user.season,
                uuid: user.uuid,
            }, { start_time: -1}, 10);
            setSessions(sessions);
        }
        async function getDefenseHistory() {
            const defenses = await aggregate('async_pvp_elo_history', {
                $match: {
                    season: user.season,
                    uuid: user.uuid,
                    type: 2,
                }
            }, {$sort: {time: -1}}, {$limit: 10},
            {
                $addFields: {
                    sessionId: "$session",
                }
            },
            {
                $lookup: {
                    from: 'async_pvp_session',
                    localField: 'session',
                    foreignField: '_id',
                    as: 'session',
                }
            }, {$unwind: '$session'});
            setDefenses(defenses);
        }
        getUserInfo();
        getSessions();
        getDefenseHistory();
    }, []);
    useEffect(() => {
        if (!userInfo) {
            return;
        }
        async function getSeasonRank() {
            const ranks = await aggregate('async_pvp_season_user', {
                $match: {
                    season: user.season,
                    group: userInfo.group,
                    elo: {$gt: userInfo.elo},
                }
            }, {$count: 'count'});
            setSeasonRank(ranks.length === 0 ? 1 : ranks[0].count + 1);
        }
        getSeasonRank();
    }, [userInfo]);
    useEffect(() => {
        async function getDailyRanks() {
            const dailyElos = await search('async_pvp_daily_elo', {
                season: user.season,
                uuid: user.uuid,
            }, {day: -1}, 10);
            const dailyRanks = [];
            for (const dailyElo of dailyElos) {
                const ranks = await aggregate('async_pvp_daily_elo', {
                    $match: {
                        season: user.season,
                        group: dailyElo.group,
                        day: dailyElo.day,
                        elo: {$gt: dailyElo.elo},
                    },
                }, {$count: 'count'});
                dailyRanks.push({
                    day: dailyElo.day,
                    rank: ranks.length === 0 ? 1 : ranks[0].count + 1,
                    elo: dailyElo.elo,
                });
            }
            setDailyRanks(dailyRanks);
        }
        getDailyRanks();
    }, [userInfo]);
    const sessionColumns = useMemo(() => {
        return [{
            Header: 'Status',
            accessor: 'status',
            Cell: ({value}) => value === 0 ? 'Playing' : 'Finished',
            width: 100,
        }, {
            Header: 'Start Time',
            accessor: 'start_time',
            Cell: ({value}) => new Date(value).toLocaleString(),
            width: 200,
        }, {
            Header: 'Opponents',
            Cell: function MyCell({original: session}) {
                return <ol>
                    {
                        session.finished_opponents.map((opponent, i) => {
                            const result = session.results[i];
                            return <li key={i}>
                                <User season={season} uuid={opponent.uuid} name={opponent.name} /> === <Result value={result} />
                            </li>;
                        })
                    }
                    {
                        session.status === 0 && session.current_opponent &&
                        <li>
                            <User season={season} uuid={session.current_opponent.uuid} name={session.current_opponent.name} />
                        </li>
                    }
                </ol>;
            },
        }];
    }, []);
    const defenseColumns = useMemo(() => {
        return [{
            Header: 'Time',
            accessor: 'time',
            Cell: ({value}) => new Date(value).toLocaleString(),
        }, {
            Header: 'Attacker',
            accessor: 'session.uuid',
            Cell: function MyCell({value}) { return <User uuid={value} /> },
        }, {
            Header: 'Result',
            accessor: 'change',
            Cell: function MyCell({value}) { return <Change value={value} />},
        }];
    });
    const createSessionSubComponent = useCallback(({original: session}) => {
        return <div>
            <SessionInfo session={session} />
        </div>;
    }, []);
    const createDefenseSubComponent = useCallback(({original: defense}) => {
        return <div>
            <SessionInfo session={defense.session} />
        </div>;
    }, []);
    const seasonLeaderBoardUrl = useMemo(() => {
        if (!userInfo) {
            return '';
        }
        return `/async_pvp_leader_board?season_id=${user.season}&group_id=${userInfo.group}&type=season`;
    }, [userInfo]);
    return <div style={{display: 'flex'}}>
        {
            userInfo &&
            <div>
                <h3>User Info</h3>
                <div><User uuid={userInfo.uuid} /></div>
                <div>Season: <a href={seasonLeaderBoardUrl}>{userInfo.season}</a></div>
                <div>League: {userInfo.league}</div>
                <div>Group: <a href={seasonLeaderBoardUrl}>{userInfo.group}</a></div>
                <div>Season <a href={seasonLeaderBoardUrl}>Rank: {seasonRank}, Elo: {userInfo.elo}, MMR: {userInfo.match_making_rating}</a></div>
                {
                    dailyRanks.map(({day, rank, elo}) => {
                        const url = `/async_pvp_leader_board?season_id=${user.season}&group_id=${userInfo.group}&type=today&day_id=${day}`;
                        return <div key={day}>Daily {day} <a href={url}>Rank: {rank}, Elo: {elo}</a></div>;
                    })
                }
                <div>Board Win: {userInfo.board_win_count}</div>
                <div>Board Lose: {userInfo.board_lose_count}</div>
                <div>Board Time: {new Date(userInfo.board_time).toLocaleString()}</div>
                <div>Protected Until: <ProtectedUntil value={userInfo.protected_until} /></div>
                <pre>
                    {
                        safeFormatJson(userInfo.board)
                    }
                </pre>
            </div>
        }
        <div style={{flexGrow: 1}}>
            <h3>Attack History</h3>
            <ReactTable
                data={sessions}
                columns={sessionColumns}
                pageSize={sessions.length}
                showPagination={false}
                SubComponent={createSessionSubComponent}
            />
        </div>
        <div style={{flexGrow: 1}}>
            <h3>Defense History</h3>
            <ReactTable
                data={defenses}
                columns={defenseColumns}
                pageSize={defenses.length}
                showPagination={false}
                SubComponent={createDefenseSubComponent}
            />
        </div>
    </div>;
}

export function AsyncPvpUser() {
    const params = new URLSearchParams(window.location.search);
    const uuid = params.get('uuid');
    const season = params.get('season');
    return <SeasonContext.Provider value={season}>
        <UserComponent uuid={uuid} />
    </SeasonContext.Provider>;
}

function useQueryParameter(name, defaultValue) {
    const [storageValue, setStorageValue] = useLocalStorageState(name, {defaultValue: defaultValue});
    const value = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get(name) ?? storageValue;
    }, [storageValue]);
    const setValue = useCallback((value) => {
        const params = new URLSearchParams(window.location.search);
        params.set(name, value);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        setStorageValue(value);
    }, []);
    return [value, setValue];
}

export function AsyncPvpLeaderBoard() {
    const [seasons, setSeasons] = useState([]);
    const [seasonId, setSeasonId] = useQueryParameter('season_id');
    const season = useMemo(() => {
        return seasons.find((season) => season._id === seasonId);
    }, [seasons, seasonId]);
    const setSeason = useCallback((season) => {
        setSeasonId(season._id);
    }, []);
    const [groups, setGroups] = useState([]);
    const [groupId, setGroupId] = useQueryParameter('group_id');
    const group = useMemo(() => {
        return groups.find((group) => group._id === groupId);
    }, [groups, groupId]);
    const setGroup = useCallback((group) => {
        setGroupId(group._id);
    }, []);
    const [type, setType] = useQueryParameter('type', 'season');
    const [days, setDays] = useState([]);
    const [dayId, setDayId] = useQueryParameter('day_id');
    const day = useMemo(() => {
        return days.find((day) => day._id === dayId);
    }, [days, dayId]);
    const setDay = useCallback((day) => {
        setDayId(day._id);
    }, []);
    const [users, setUsers] = useState([]);
    useEffect(() => {
        async function run() {
            const seasons = await search('async_pvp_season', {}, { start_time: -1 }, 10);
            setSeasons(seasons);
        }
        run();
    }, []);
    const seasonOptions = useMemo(() => {
        return seasons.map((season) => {
            return {
                value: season._id,
                label: formatSeasonLable(season),
            };
        });
    }, [seasons]);
    const selectedSeason = useMemo(() => {
        if (!season) {
            return { value: '', label: 'Select season' };
        }
        return { value: season._id, label: formatSeasonLable(season) };
    }, [season]);
    const selectSeason = useCallback((value) => {
        setSeason(seasons.find((s) => s._id === value.value));
    }, [seasons]);
    useEffect(() => {
        if (!season) {
            return;
        }
        async function run() {
            const groups = await aggregate('async_pvp_season_user', {
                $match: {
                    season: season._id,
                },
            }, {
                $group: {
                    _id: '$group',
                    league: { $first: '$league' },
                    count: { $sum: 1 },
                }
            }, {
                $sort: {
                    _id: 1,
                }
            });
            setGroups(groups);

            const days = await aggregate('async_pvp_daily_elo', {
                $match: {
                    season: season._id,
                },
            }, {
                $group: {
                    _id: '$day',
                    count: { $sum: 1 },
                }
            }, {
                $sort: {
                    _id: 1,
                }
            });
            setDays(days);
        }
        run();
    }, [season]);
    const groupOptions = useMemo(() => {
        return groups.map((group) => {
            return {
                value: group._id,
                label: `${group._id} (player=${group.count},league=${group.league})`,
            };
        });
    }, [groups]);
    const selectedGroup = useMemo(() => {
        if (!group) {
            return { value: '', label: 'Select group' };
        }
        return { value: group._id, label: `${group._id} (player=${group.count},league=${group.league})` };
    }, [group]);
    const selectGroup = useCallback((value) => {
        setGroup(groups.find((g) => g._id === value.value));
    }, [groups]);
    const dayOptions = useMemo(() => {
        return days.map((day) => {
            return {
                value: day._id,
                label: `${day._id} (${day.count})`,
            };
        });
    }, [days]);
    const selectedDay = useMemo(() => {
        if (!day) {
            return { value: '', label: 'Select day' };
        }
        return { value: day._id, label: `${day._id} (${day.count})` };
    }, [day]);
    const selectDay = useCallback((value) => {
        setDay(days.find((d) => d._id === value.value));
    }, [days]);
    useEffect(() => {
        if (!season || !group) {
            return;
        }
        if (type === 'today' && !day) {
            return;
        }
        async function run() {
            let users = [];
            if (type === 'season') {
                users = await aggregate('async_pvp_season_user', {
                    $match: {
                        season: season._id,
                        group: group._id,
                    },
                }, {
                    $sort: {
                        elo: -1,
                    }
                }, { $limit: 100 }, {
                    $lookup: {
                        from: 'user',
                        localField: 'uuid',
                        foreignField: '_id',
                        as: 'user',
                    },
                }, { $unwind: '$user' });
            } else {
                users = await aggregate('async_pvp_daily_elo', {
                    $match: {
                        season: season._id,
                        group: group._id,
                        day: day._id,
                    },
                }, {
                    $sort: {
                        elo: -1,
                    }
                }, { $limit: 100 }, {
                    $lookup: {
                        from: 'user',
                        localField: 'uuid',
                        foreignField: '_id',
                        as: 'user',
                    },
                }, { $unwind: '$user' });
            }
            setUsers(users);
            console.log(users);
        }
        run();
    }, [season, group, type, day]);

    const columns = useMemo(() => {
        function setElo(uuid) {
            return () => {
                const payload = {
                    collection: 'async_pvp_season_user',
                    query: {
                        season: season._id,
                        uuid,
                    },
                    update: {
                        $set: {
                            elo: -10000,
                        }
                    },
                    add: {},
                };
                const url = `/update#${encodeURIComponent(JSON.stringify(payload))}`;
                window.open(url, '_blank');
            };
        }
        let result = [
            {
                Header: 'Rank',
                id: 'rank',
                Cell: row => row.index + 1,
                width: 50,
            },
            {
                Header: 'Id',
                accessor: 'uuid',
                Cell: function MyCell(row) { return <a href={`/async_pvp_user?season=${season._id}&uuid=${row.value}`}>{row.value}</a>; },
                width: 350,
            },
            {
                Header: 'Name',
                accessor: 'user.name',
                width: 100,
            },
            {
                Header: 'Elo',
                accessor: 'elo',
                width: 150,
            },
            {
                Header: 'MMR',
                accessor: 'match_making_rating',
                width: 150,
            },
        ];
        if (type === 'season') {
            result = result.concat([
            {
                Header: 'Board Win',
                accessor: 'board_win_count',
            },
            {
                Header: 'Board Lose',
                accessor: 'board_lose_count',
            },
            {
                Header: 'Protection',
                accessor: 'protected_until',
                Cell: function MyCell(row) { return <ProtectedUntil value={row.value} />; },
            },
            ]);
        }
        result = result.concat([
            {
                Header: 'Operation',
                id: 'operation',
                Cell: function MyCell(row) {
                    return <Button variant="contained" onClick={setElo(row.original.uuid)}>Set elo to -10000</Button>;
                },
            }
        ]);
        return result;
    }, [season, type]);
    return <div>
        <div>
            Season:
            <LazySelect options={seasonOptions} value={selectedSeason} onChange={selectSeason} />
        </div>
        {
            season &&
            <div>
                Group:
                <LazySelect options={groupOptions} value={selectedGroup} onChange={selectGroup} />
            </div>
        }
        {
            season && group &&
            <div>
                Type:
                <LazySelect options={[{ value: 'season', label: 'season' }, { value: 'today', label: 'daily' }]} value={{ value: type, label: type }} onChange={(value) => setType(value.value)} />
            </div>
        }
        {
            season && group && type === 'today' &&
            <div>
                Day:
                <LazySelect options={dayOptions} value={selectedDay} onChange={selectDay} />
            </div>
        }
        {
            users.length > 0 &&
            <div>
                <SeasonContext.Provider value={season._id}>
                    <ReactTable
                        data={users}
                        columns={columns}
                        pageSize={users.length}
                        showPagination={false}
                    />
                </SeasonContext.Provider>
            </div>
        }
    </div>;
}
